import axios from "axios";
import { safeGetItem, safeSetItem, safeRemoveItem } from "./safeStorage";

// ─── Token helpers ────────────────────────────────────────────────────────────

const TOKEN_KEY = "clinica_token";

// Bug real corrigido 27/08/2026: acesso direto ao localStorage sem try/catch
// derrubava a árvore React inteira (ErrorBoundary genérico, "Algo deu errado" /
// "The object can not be found here.") em qualquer navegador que restrinja
// storage no momento (Safari em modo privado antigo, bateria crítica com
// battery saver agressivo — bate com o print real que expôs este bug: paciente
// com bateria em vermelho —, quota cheia, política de dispositivo/MDM). Como
// getToken() roda no interceptor do axios em TODA requisição (linha ~155), uma
// falha aqui quebrava qualquer ação, não só login — inclusive editar dados de
// médico no admin, sem relação nenhuma com o dado sendo editado. safeGet/
// safeSet engolem a exceção e caem no comportamento mais seguro (sessão não
// encontrada → trata como deslogado, em vez de crashar a página toda).
// Helpers movidos pra `lib/safeStorage.ts` (06/09/2026) — mesmo padrão agora
// reaproveitado nos outros ~30 arquivos que acessavam localStorage cru.

// O token também é mantido em memória (variável de módulo) enquanto a página
// estiver aberta — além do localStorage usado para restaurar a sessão ao
// reabrir/refresh. POR QUÊ: `safeGetItem()` engole exceção e devolve null
// quando o storage fica indisponível no meio da sessão (battery saver
// agressivo, Safari em modo privado antigo, quota cheia, política de
// dispositivo/MDM — o mesmo cenário do bug 27/08/2026 descrito acima). Com o
// token guardado só no localStorage, `getToken()` passava a devolver null a
// qualquer momento e TODA tela que fazia `if (!getToken())` — ex. as páginas
// do Portal do Paciente (/paciente/meusdados, /paciente/ajuda,
// /paciente/historico, /paciente/notificacoes, ...) — redirecionava de volta
// pro início: o clique em "Meus dados", "Ajuda", "Histórico" etc. "não
// funcionava". A memória garante que o token continua válido durante a sessão
// atual mesmo que o storage falhe; o localStorage segue sendo a fonte da
// verdade quando está disponível (logout entre abas, troca de conta e
// expiração continuam se comportando como antes).
let memoryToken: string | null = null;

// ─── Login por área depois de uma troca de conta (31/10/2026) ─────────────────
// O login era um só para o navegador inteiro. Quem troca de conta (admin ↔ paciente, ou médico que também é paciente)
// e deixa as duas abas abertas ficava com a última troca valendo para TODAS as abas: a aba do paciente passava a pedir
// com o login de admin (403 nas rotas de paciente — o Blink+ e o contador do chat sumiam "às vezes") e vice-versa.
// Agora, depois de uma troca de conta (setSwitchedToken), cada área guarda o seu login: /paciente, a fila, o pagamento
// etc. usam o de paciente; /medico, /admin e o prontuário usam o de médico/admin. Login novo (setToken) ou "Sair"
// (clearToken) apagam os dois — nada passa de uma pessoa para outra no mesmo aparelho.
const PATIENT_SLOT_KEY = "clinica_token_patient";
const STAFF_SLOT_KEY = "clinica_token_staff";
const PATIENT_AREAS = ["/paciente", "/consultar", "/fila", "/sala-espera", "/obrigado", "/pagamento", "/payment", "/checkout", "/chat"];
const STAFF_AREAS = ["/medico", "/admin", "/prontuario", "/marketing"];

function roleOfToken(t: string | null): string {
  if (!t) return "";
  try {
    const b64 = t.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const p = JSON.parse(atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4)));
    return p.role === "patient" || p.type === "patient" ? "patient" : String(p.role || "");
  } catch { return ""; }
}
const slotKeyFor = (t: string) => (roleOfToken(t) === "patient" ? PATIENT_SLOT_KEY : STAFF_SLOT_KEY);

function areaSlotKey(): string | null {
  if (typeof window === "undefined") return null;
  const p = window.location.pathname;
  // Abas só de médico dentro da consulta (Memed e Prescrição Blink em tela cheia).
  if (/^\/consulta\/[^/]+\/(prescricao|documento)\/?$/.test(p)) return STAFF_SLOT_KEY;
  const inArea = (list: string[]) => list.some((a) => p === a || p.startsWith(`${a}/`));
  if (inArea(PATIENT_AREAS)) return PATIENT_SLOT_KEY;
  if (inArea(STAFF_AREAS)) return STAFF_SLOT_KEY;
  return null;
}

export function getToken(): string | null {
  try {
    const stored = localStorage.getItem(TOKEN_KEY);
    // Storage disponível → é a fonte da verdade (preserva o comportamento
    // entre abas: se outra aba apagou/trocou o token, esta aba respeita).
    if (stored !== null) {
      memoryToken = stored;
      // Depois de uma troca de conta, a área da tela escolhe o login dela (ver acima).
      const slot = areaSlotKey();
      const areaToken = slot ? localStorage.getItem(slot) : null;
      return areaToken || stored;
    }
    return null;
  } catch {
    // Storage indisponível no momento → usa o token mantido em memória nesta
    // sessão (setToken sempre grava na memória antes do localStorage).
    return memoryToken;
  }
}

export function setToken(t: string): void {
  // Grava na memória PRIMEIRO — mesmo que o localStorage falhe (storage
  // indisponível), a sessão atual continua autenticada.
  memoryToken = t;
  safeSetItem(TOKEN_KEY, t);
  // Login novo: sem login guardado por área (pode ser outra pessoa no mesmo aparelho).
  safeRemoveItem(PATIENT_SLOT_KEY);
  safeRemoveItem(STAFF_SLOT_KEY);
}

// Aba do paciente aberta só com o login de médico/admin (antes de existir o login por área, ou a troca foi feita em
// outro aparelho): busca o login de paciente da MESMA pessoa — conta vinculada, quem confere é o servidor, igual ao
// "Trocar de conta" — e guarda na área do paciente, sem mexer no login das outras abas. Uma tentativa por login.
let patientRecovery: Promise<string | null> | null = null;
let patientRecoveryFailedFor: string | null = null;
function recoverPatientToken(): Promise<string | null> {
  if (areaSlotKey() !== PATIENT_SLOT_KEY) return Promise.resolve(null);
  const staff = getToken();
  const role = roleOfToken(staff);
  if (!staff || !role || role === "patient" || patientRecoveryFailedFor === staff) return Promise.resolve(null);
  if (!patientRecovery) {
    patientRecovery = axios.post("/api/auth/switch-experience", {}, { headers: { Authorization: `Bearer ${staff}` }, timeout: 15000 })
      .then((r) => {
        const d = r.data?.data ?? r.data;
        const t: string | undefined = d?.token;
        if (!t || roleOfToken(t) !== "patient") { patientRecoveryFailedFor = staff; return null; }
        safeSetItem(STAFF_SLOT_KEY, staff);
        safeSetItem(PATIENT_SLOT_KEY, t);
        return t;
      })
      .catch(() => { patientRecoveryFailedFor = staff; return null; })
      .finally(() => { patientRecovery = null; });
  }
  return patientRecovery;
}

/** Troca de conta da MESMA pessoa (POST /auth/switch-experience): guarda o login de cada área. */
export function setSwitchedToken(next: string): void {
  try {
    const prev = localStorage.getItem(TOKEN_KEY);
    if (prev && roleOfToken(prev) && roleOfToken(prev) !== roleOfToken(next)) safeSetItem(slotKeyFor(prev), prev);
  } catch { /* sem storage: fica só a memória */ }
  memoryToken = next;
  safeSetItem(TOKEN_KEY, next);
  safeSetItem(slotKeyFor(next), next);
}

// Login de paciente sempre precisa gravar as DUAS coisas juntas: o token (auth) e
// `logged_patient_case_id` (usado por várias telas — /paciente, /chat, /consulta,
// /sala-espera, useAuth — pra restaurar a sessão sem decodificar o token toda vez).
// Antes cada tela de login gravava isso manualmente e de forma inconsistente (alguns
// pontos esqueciam o case_id), causando "sessão escondida": token válido salvo, mas
// telas que dependiam só do case_id não reconheciam o paciente como logado. Centralizado
// aqui pra nunca mais faltar uma das duas.
export function persistPatientSession(token: string, patientId: number | string): void {
  setToken(token);
  safeSetItem("logged_patient_case_id", JSON.stringify(Number(patientId)));
}

export function clearToken(): void {
  memoryToken = null;
  safeRemoveItem(TOKEN_KEY);
  safeRemoveItem(PATIENT_SLOT_KEY);
  safeRemoveItem(STAFF_SLOT_KEY);
  safeRemoveItem(ADMIN_SWITCH_KEY);
}

// ─── Admin view-mode helpers ──────────────────────────────────────────────────
// Admins podem alternar entre a experiência de admin e a de médico.
// A última escolha persiste entre sessões (fecha e abre de novo no mesmo modo).

const ADMIN_VIEW_MODE_KEY = "clinica_admin_view_mode";

export function getAdminViewMode(): "admin" | "doctor" {
  return safeGetItem(ADMIN_VIEW_MODE_KEY) === "doctor" ? "doctor" : "admin";
}

export function setAdminViewMode(mode: "admin" | "doctor"): void {
  safeSetItem(ADMIN_VIEW_MODE_KEY, mode);
}

// ─── Sessão de admin "vendo como paciente" ────────────────────────────────────
// Só admins podem trocar de conta (paciente/médico/admin). Quando o admin troca pra
// experiência de paciente o token vira de paciente (papel "patient"), então o switcher
// não conseguiria mais se identificar como admin só olhando o JWT. Essa flag lembra que
// a sessão atual veio de um admin — assim o ícone continua visível em /paciente e o
// admin consegue voltar pra médico/admin sem precisar logar de novo.

const ADMIN_SWITCH_KEY = "clinica_admin_switch";

/** True quando a sessão atual é de um admin que trocou pra experiência de paciente. */
export function isAdminAccountSwitch(): boolean {
  // Também quando o login de médico/admin guardado (troca de conta, ver setSwitchedToken) é de admin: a aba do paciente
  // continua mostrando "Admin" no Trocar de conta mesmo depois de outra aba ter voltado para o admin.
  return safeGetItem(ADMIN_SWITCH_KEY) === "1" || roleOfToken(safeGetItem(STAFF_SLOT_KEY)) === "admin";
}

export function setAdminAccountSwitch(v: boolean): void {
  if (v) safeSetItem(ADMIN_SWITCH_KEY, "1");
  else safeRemoveItem(ADMIN_SWITCH_KEY);
}


// ─── Experiência Paciente↔Médico ──────────────────────────────────────────────
// Contas com mesmo email/telefone/CPF nas duas tabelas podem alternar entre a
// experiência de paciente e de médico. Última escolha persiste entre sessões.

const LAST_EXPERIENCE_KEY = "clinica_last_experience";

export function getLastExperience(): "patient" | "doctor" | null {
  const v = safeGetItem(LAST_EXPERIENCE_KEY);
  return v === "patient" || v === "doctor" ? v : null;
}

export function setLastExperience(mode: "patient" | "doctor"): void {
  safeSetItem(LAST_EXPERIENCE_KEY, mode);
}

/** Decode JWT payload without verification (client-side only). */
export function getUser<T = Record<string, unknown>>(): T | null {
  const token = getToken();
  if (!token) return null;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    // Payload JWT é base64url (usa - e _ em vez de + e /) — converte para
    // base64 puro antes do atob.
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    // `atob` devolve bytes como Latin-1 — para nomes com acento (ex.: "João")
    // era preciso decodificar o payload como UTF-8, senão viravam mojibake
    // ("JoÃ£o") no painel. TextDecoder aplica o UTF-8 correto.
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    const decoded = JSON.parse(new TextDecoder("utf-8").decode(bytes));
    return decoded as T;
  } catch {
    return null;
  }
}

// ─── Axios instance ───────────────────────────────────────────────────────────

const api = axios.create({
  baseURL: "/api",
  headers: { "Content-Type": "application/json" },
  timeout: 20000,
});

/**
 * O token fica numa única chave do localStorage compartilhada entre paciente/
 * médico/admin (necessário pro admin poder "ver como médico" sem duplo login).
 * Isso é frágil com múltiplas abas: logar como paciente numa aba sobrescreve
 * o token que a aba do médico ainda está usando, e toda chamada de médico
 * passa a falhar (id de paciente não é UUID válido pro backend) com erros
 * confusos e diferentes a cada vez. Detectamos aqui, na saída, antes de gastar
 * uma request — TokenRoleMismatchError é tratado de forma central no response
 * interceptor abaixo.
 */
class TokenRoleMismatchError extends Error {
  constructor() {
    super("TOKEN_ROLE_MISMATCH");
    this.name = "TokenRoleMismatchError";
  }
}

const DOCTOR_ONLY_QUEUE_PATHS = [/^\/queue\/list$/, /^\/queue\/take-next$/, /^\/queue\/\d+\/accept$/];
// Rotas sob /doctor/ que são públicas por design (chamadas por pacientes sem token de
// médico/admin — ex: escolher horário disponível em /paciente) — não exigem papel nenhum.
const PUBLIC_DOCTOR_PATHS = [/^\/doctor\/schedule\/[^/]+$/, /^\/doctor\/[^/]+\/available-slots$/];

function requiredRoleForPath(path: string): "doctor_or_admin" | "admin" | "marketing_or_admin" | "nutri_or_admin" | "finance_or_admin" | null {
  if (PUBLIC_DOCTOR_PATHS.some((re) => re.test(path))) return null;
  // Perfil FINANCEIRO (consultor, 18/10/2026): só a parte de informações do Financeiro.
  // /admin/finance/payouts (repasses, com chave PIX) segue só admin.
  if (path === "/admin/finance" || /^\/admin\/finance\/(sheet|entries|notes)(\/|$)/.test(path)) return "finance_or_admin";
  // Perfil de marketing (somente leitura) também lê o painel de marketing.
  if (path === "/admin/marketing") return "marketing_or_admin";
  if (path.startsWith("/admin/emagrecimento")) return "nutri_or_admin";
  if (path.startsWith("/admin/")) return "admin";
  if (path.startsWith("/doctor/")) return "doctor_or_admin";
  if (DOCTOR_ONLY_QUEUE_PATHS.some((re) => re.test(path))) return "doctor_or_admin";
  return null;
}

function tokenMatchesRole(required: "doctor_or_admin" | "admin" | "marketing_or_admin" | "nutri_or_admin" | "finance_or_admin"): boolean {
  const decoded = getUser<{ role?: string }>();
  const role = decoded?.role;
  if (required === "finance_or_admin") return role === "admin" || role === "financeiro";
  if (required === "marketing_or_admin") return role === "admin" || role === "marketing";
  if (required === "nutri_or_admin") return role === "admin" || role === "nutricionista";
  // Secretária (17/10/2026) entra no /admin com acesso só às rotas de consultas; o backend
  // (staffOrAdmin) decide e devolve 403 no resto. Não pode cair no "sessão trocada em outra
  // aba" por chamar uma rota de admin — por isso passa daqui e quem barra é o servidor.
  if (required === "admin") return role === "admin" || role === "secretaria";
  return role === "doctor" || role === "admin";
}

// Inject Bearer token on every request; bloqueia chamadas de médico/admin
// disparadas com um token de outro papel (ver TokenRoleMismatchError acima)
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers = (config.headers || {}) as import("axios").AxiosRequestHeaders;
    (config.headers as Record<string, string>)["Authorization"] = `Bearer ${token}`;

    const required = requiredRoleForPath(config.url || "");
    if (required && !tokenMatchesRole(required)) {
      return Promise.reject(new TokenRoleMismatchError());
    }
  }
  return config;
});

// Desempacotar envelope { success, data } automaticamente
// Respostas da API usam ok() → { success: true, data: ... }
// O frontend espera res.data com os campos direto
api.interceptors.response.use(
  (res) => {
    if (
      res.data &&
      typeof res.data === "object" &&
      res.data.success === true &&
      "data" in res.data
    ) {
      res.data = res.data.data;
    }
    return res;
  },
  (err) => {
    if (err instanceof TokenRoleMismatchError) {
      // Token de outro papel (ex: paciente logou em outra aba e sobrescreveu
      // o token que esta aba de médico/admin estava usando) — ou uma chamada
      // de médico disparada por engano numa tela de paciente. NÃO limpa o
      // token aqui: em telas com papéis mistos (ex: /consulta, onde paciente
      // e médico coexistem) isso derrubaria uma sessão válida por causa de
      // uma chamada indevida. Só a tela de médico/admin, ao ver o evento,
      // decide se precisa forçar novo login.
      window.dispatchEvent(new CustomEvent("clinica:token-role-mismatch"));
    }
    // Bug real corrigido 28/08/2026: tokens novos não expiram mais (pedido do
    // dono), mas sessões ABERTAS ANTES desse deploy ainda carregam o token
    // antigo com `exp` — quando ele vence, o middleware (auth.js) responde 401
    // "Token expirado"/"Token inválido" pra TODA chamada. Como a regra geral é
    // "nunca deslogar automaticamente em 401" (linha abaixo), a pessoa ficava
    // presa vendo esse erro pra sempre, sem saber que precisava logar de novo
    // (caso real: Dra. Maria Gabriela travada no meio de uma consulta). Este é
    // o ÚNICO caso em que vale deslogar automaticamente — o token está
    // genuinamente inválido, continuar "logado" na tela é pior que mostrar o
    // login. Checa a mensagem exata (não confundir com 401/403 de permissão,
    // ex.: 403 de admin-only, que não deve deslogar).
    const serverMsg = err?.response?.data?.error;
    if (err?.response?.status === 401 && (serverMsg === "Token expirado" || serverMsg === "Token inválido")) {
      clearToken();
      window.dispatchEvent(new CustomEvent("clinica:session-expired"));
    }
    // Login sumiu do aparelho (28/10/2026, caso Maiara: o navegador apagou o armazenamento logo depois do login pelo link
    // do WhatsApp). A tela seguia "logada" e toda ação dava 401 "Token não fornecido", sem caminho de volta. Só avisa:
    // o portal do paciente decide (sai para o login apenas se havia paciente na tela).
    if (err?.response?.status === 401 && serverMsg === "Token não fornecido") {
      window.dispatchEvent(new CustomEvent("clinica:session-missing"));
    }
    // Tela de paciente pedindo com o login de médico/admin → busca o login de paciente e repete o pedido (ver
    // recoverPatientToken). Era o "Acesso restrito a pacientes." do presentinho e o Blink+ sumindo (31/10/2026).
    const cfgRetry = err?.config as (Record<string, unknown> & { _patientRetry?: boolean }) | undefined;
    if (err?.response?.status === 403 && serverMsg === "Acesso restrito a pacientes." && cfgRetry && !cfgRetry._patientRetry) {
      return recoverPatientToken().then((t) => {
        if (!t) return Promise.reject(err);
        return api.request({ ...(cfgRetry as object), _patientRetry: true } as import("axios").AxiosRequestConfig);
      });
    }
    // Secretária sem o termo de sigilo assinado (ou termo atualizado): o /admin mostra o termo.
    if (err?.response?.status === 403 && err?.response?.data?.code === "TERM_REQUIRED") {
      window.dispatchEvent(new CustomEvent("clinica:team-term-required"));
    }
    // Admin sem o PIN confirmado (ou PIN vencido, 12 h): o /admin mostra a tela do PIN (24/10/2026).
    if (err?.response?.status === 403 && err?.response?.data?.code === "ADMIN_PIN_REQUIRED") {
      window.dispatchEvent(new CustomEvent("clinica:admin-pin-required"));
    }
    // Fora desse caso específico: nunca deslogar em 401/403 — só rejeitar a promise
    return Promise.reject(err);
  }
);

// ─── Auth API ─────────────────────────────────────────────────────────────────

export const authAPI = {
  /** Link do aviso de chat (WhatsApp, 18/10/2026): troca o código ?k= por sessão de paciente. */
  magicLogin: (code: string) => api.post("/auth/magic-login", { code }),

  /** Login médico/admin: { email, senha } → { token, doctor } */
  loginDoctor: (d: { email: string; senha: string }) =>
    api.post("/auth/login", d),

  /** Login paciente: { email|whatsapp, senha } → { token, patient } */
  loginPatient: (d: { email?: string; whatsapp?: string; senha: string }) =>
    api.post("/patient/login", {
      email: d.email || d.whatsapp,
      password: d.senha,
    }),

  /** Registro de médico (admin) */
  registerDoctor: (d: Record<string, unknown>) =>
    api.post("/auth/register/doctor", d),

  /** Cadastro público de médico — fica na fila (pending) */
  registerDoctorPublic: (d: Record<string, unknown>) =>
    api.post("/auth/register/doctor/public", d),

  /** Reset de senha médico */
  resetPasswordDoctor: (d: { email: string }) =>
    api.post("/auth/reset-password/doctor", d),

  /** Reset de senha paciente */
  resetPasswordPatient: (d: { email?: string; whatsapp?: string }) =>
    api.post("/auth/reset-password/patient", d),

  /** Solicitar reset (termos) */
  requestReset: (d: Record<string, unknown>) =>
    api.post("/auth/request-reset", d),

  /** Completar reset de senha */
  resetPassword: (d: Record<string, unknown>) =>
    api.post("/auth/reset-password", d),

  /** Completar reset de senha */
  completePasswordReset: (d: Record<string, unknown>) =>
    api.post("/auth/reset-password", d),

  /** Login por WhatsApp + código (OTP) — passo 1: envia o código */
  loginEnviarCodigo: (d: { whatsapp: string }) =>
    api.post("/auth/login/enviar-codigo", d),

  /** Login por WhatsApp + código (OTP) — passo 2: verifica e emite token */
  loginVerificarCodigo: (d: { whatsapp: string; code: string }) =>
    api.post("/auth/login/verificar-codigo", d),

  /** Checa se a conta logada (paciente OU médico) também tem conta do outro tipo */
  checkDualAccount: () => api.get("/auth/dual-account"),

  /** Troca o token atual pra conta do outro tipo (paciente↔médico) */
  switchExperience: (password?: string) => api.post("/auth/switch-experience", password ? { password } : {}),
};

// ─── Doctor API (autenticado) ─────────────────────────────────────────────────

export const cidAPI = {
  search: (q: string) => api.get("/cid10/search", { params: { q } }),
  getByCode: (code: string) => api.get(`/cid10/${encodeURIComponent(code)}`),
};

export const doctorAPI = {
  getProfile: () => api.get("/doctor/profile"),
  updateProfile: (d: Record<string, unknown>) => api.put("/doctor/profile", d),
  changePassword: (d: { current_password: string; new_password: string }) =>
    api.put("/doctor/change-password", d),
  deleteAccount: () => api.delete("/doctor/account"),

  getWhatsappGroups: () => api.get("/doctor/whatsapp-groups"),

  /** Aviso (não-bloqueante) de certificado digital ICP-Brasil — ver doctorController.js */
  getCertificateStatus: () => api.get("/doctor/certificate-status"),

  /** Confirmar certificado digital BirdID/VidaaS (auto-declaração pelo sino de notificações) */
  confirmCertificate: (d: { provider: "birdid" | "vidaas" }) =>
    api.put("/doctor/certificate/confirm", d),

  getPatients: () => api.get("/doctor/patients"),
  syncPatients: (d: Record<string, unknown>) => api.post("/doctor/patients/sync", d),
  addPatientManual: (d: Record<string, unknown>) => api.post("/doctor/patients/manual", d),
  updatePatient: (id: string | number, d: Record<string, unknown>) =>
    api.put(`/doctor/patients/${id}`, d),
  removePatient: (id: string | number) => api.delete(`/doctor/patients/${id}`),
  createInviteLink: (d: Record<string, unknown>) => api.post("/doctor/patients/invite", d),

  getRecords: (caseId: string | number) =>
    api.get(`/doctor/patients/${caseId}/records`),
  createRecord: (caseId: string | number, d: Record<string, unknown>) =>
    api.post(`/doctor/patients/${caseId}/records`, d),
  updateRecord: (id: string | number, d: Record<string, unknown>) =>
    api.put(`/doctor/records/${id}`, d),

  getExams: (caseId: string | number) =>
    api.get(`/doctor/patients/${caseId}/exams`),
  uploadExam: (caseId: string | number, formData: FormData) =>
    api.post(`/doctor/patients/${caseId}/exams`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),

  getAppointments: () => api.get("/doctor/appointments"),
  /** Versão leve pro painel principal — só ativas + concluídas dos últimos 60
      dias, campos mínimos (ver doctorController.js → getAppointmentsDashboard).
      Corrige lentidão real do painel em rede ruim (payload ~10x menor). */
  getAppointmentsDashboard: () => api.get("/doctor/appointments/dashboard"),
  /** Busca 1 consulta específica (mesmos campos completos de getAppointments) —
      usar em vez de getAppointments()+find() quando só 1 registro é necessário
      (ex.: página de prescrição). Muito mais leve que baixar o histórico inteiro. */
  getAppointmentById: (id: string | number) => api.get(`/doctor/appointments/${id}`),
  /** Conduta automática (04/11/2026): manda o texto atual e recebe a conduta com o que foi emitido e ainda não entrou. */
  mergeConduct: (id: string | number, conduct: string) =>
    api.post<{ chaves: string[]; conduta?: string; resumo?: string }>(`/doctor/appointments/${id}/conduta/mesclar`, { conduct }),
  /** Confirma, depois de salvar, o que entrou: se o médico apagar a linha, ela não volta. */
  ackConduct: (id: string | number, chaves: string[]) => api.post(`/doctor/appointments/${id}/conduta/aplicados`, { chaves }),
  createAppointment: (d: Record<string, unknown>) =>
    api.post("/doctor/appointments", d),
  updateAppointment: (id: string | number, d: Record<string, unknown>) =>
    api.put(`/doctor/appointments/${id}`, d),
  deleteAppointment: (id: string | number) =>
    api.delete(`/doctor/appointments/${id}`),

  /** Médico abre a sala da consulta (POST /consultation/:id/open) */
  openAppointment: (id: string | number) =>
    api.post(`/consultation/${id}/open`),

  /** Médico encerra a videochamada só para o paciente (POST /consultation/:id/end-call-for-patient) —
      o paciente passa a ver "Consulta encerrada" e não volta ao vídeo desta consulta. */
  endCallForPatient: (id: string | number) =>
    api.post(`/consultation/${id}/end-call-for-patient`, {}),

  /** Médico registra ausência do paciente (POST /consultation/:id/no-show) */
  registerNoShow: (id: string | number, d: Record<string, unknown>) =>
    api.post(`/consultation/${id}/no-show`, d),

  /** Médico interrompe a consulta (POST /consultation/:id/interrupt) — volta ao
      estado inicial, como se a sala nunca tivesse sido aberta. */
  interruptAppointment: (id: string | number) =>
    api.post(`/consultation/${id}/interrupt`, {}),

  /** Médico finaliza a consulta (POST /consultation/:id/finalize) */
  finalizeAppointment: (id: string | number, d: Record<string, unknown>) =>
    api.post(`/consultation/${id}/finalize`, d),

  uploadSignature: (formData: FormData) =>
    api.post("/doctor/signature/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),

  getRevenue: () => api.get("/doctor/revenue"),
  getMemedToken: (prescriber: Record<string, unknown>) => api.post("/doctor/memed-token", { prescriber }),
  abrirAvulsaBlink: (d: { nome: string; cpf?: string; telefone?: string; nasc?: string; sexo?: string }) => api.post("/doctor/prescricao-avulsa/blink", d),
  getProntuarioByCase: (caseId: string | number) =>
    api.get(`/doctor/prontuario/${caseId}`),
  getMyRevalidacoes: () => api.get("/medico/revalidacoes"),

  setOnline: (d: { is_online: boolean }) => api.put("/doctor/online", d),

  /** Modo prioridade exclusiva (Dr. Felipe Ferraz) — flag discreta, só ele acessa */
  getPriorityImmediate: () => api.get("/doctor/priority-immediate"),
  setPriorityImmediate: (enabled: boolean) =>
    api.put("/doctor/priority-immediate", { enabled }),

  /** Modo de autoaceite da fila de plantão (Dr. Felipe Ferraz) — fast/slow/priority */
  getAutoacceptMode: () => api.get("/doctor/autoaccept-mode"),
  setAutoacceptMode: (mode: "fast" | "sec40" | "slow" | "min2" | "priority") =>
    api.put("/doctor/autoaccept-mode", { mode }),
  getAttendWhatsapp: () => api.get("/doctor/attend-whatsapp"),
  // immediate = consultas imediatas, scheduled = agendadas (cada uma ligada/desligada à parte).
  setAttendWhatsapp: (d: { immediate?: boolean; scheduled?: boolean }) => api.put("/doctor/attend-whatsapp", d),

  getSchedule: () => api.get("/doctor/schedule"),
  saveSchedule: (d: Record<string, unknown>) => api.put("/doctor/schedule", d),
  heartbeat: () => api.post("/doctor/heartbeat", {}),

  getWallet: () => api.get("/doctor/wallet"),
  requestWithdrawal: (d: Record<string, unknown>) =>
    api.post("/doctor/wallet/withdraw", d),
  updatePixKey: (d: { pix_key: string }) => api.put("/doctor/pix-key", d),
};

// ─── Doctor público (sem auth) ────────────────────────────────────────────────

export const doctorPublicAPI = {
  listAvailable: (params?: { specialty?: string }) =>
    api.get("/doctors/available", { params }),
  /** Perfil público de um médico (página de divulgação /d/:doctorId). */
  getProfile: (doctorId: string | number) => api.get(`/doctors/${doctorId}`),
  getSchedulePublic: (doctorId: string | number) =>
    api.get(`/doctor/schedule/${doctorId}`),
};

// ─── Patient API ──────────────────────────────────────────────────────────────

export const patientAPI = {
  login: (d: { email?: string; whatsapp?: string; senha: string }) =>
    api.post("/patient/login", {
      email: d.email || d.whatsapp,
      password: d.senha,
    }),
  register: (d: Record<string, unknown>) => api.post("/patient/register", d),
  dashboard: () => api.get("/patient/dashboard"),
  chatUnread: () => api.get("/patient/chat-unread"),
  appointmentStatus: (id: string | number) => api.get(`/patient/appointments/${id}/status`),
  registerByInvite: (d: Record<string, unknown>) =>
    api.post("/patient/register-invite", d),
  updateSelf: (d: Record<string, unknown>) => api.put("/patient/update", d),
  changePassword: (d: { new_password: string; current_password?: string }) =>
    api.put("/patient/change-password", d),
  getInviteInfo: (token: string) => api.get(`/patient/invite/${token}`),
  checkExists: (d: { email?: string; whatsapp?: string }) =>
    api.post("/patient/check-exists", d),
  getReceipts: () => api.get("/patient/receipts"),
  getDocuments: () => api.get("/patient/documents"),
  deleteAccount: () => api.delete("/patient/account"),
  sendOnboarding: (d: Record<string, unknown>) =>
    api.post("/patient/onboarding", d),
  acceptTCLE: (caseId: string | number, d: Record<string, unknown>) =>
    api.post(`/consultation/${caseId}/tcle`, d),
  getReferralCode: () => api.get("/patient/referral-code"),
  validateReferralCode: (code: string) =>
    api.post("/referral/validate", { code }),
  /** Cupons de indicação do paciente logado (portal e checkout) — 28/10/2026. */
  getCoupons: () => api.get("/patient/coupons"),

  /** Paciente entrou na sala (só depois de o médico abrir) */
  patientJoined: (id: string | number) =>
    api.post(`/consultation/${id}/patient-joined`),

  /** Status do consentimento de telemedicina da consulta */
  getTelemedicineConsent: (caseId: string | number, appointmentId?: string | number) =>
    api.get(`/consultation/${caseId}/telemedicine-consent`, { params: appointmentId ? { appointment_id: appointmentId } : {} }),

  /** Registra o consentimento específico de telemedicina da consulta */
  acceptTelemedicineConsent: (caseId: string | number, d: { appointment_id?: string | number; result?: string }) =>
    api.post(`/consultation/${caseId}/telemedicine-consent`, d),
};

// ─── Admin API ────────────────────────────────────────────────────────────────

export const badgeAPI = {
  // PIX Automático e 2ª operadora do cartão (Asaas).
  pixCreate: (d: { cpf: string }) => api.post("/badge/pix/create", d, { timeout: 60000 }),
  pixStatus: () => api.get("/badge/pix/status"),
  asaasCard: (d: { card: unknown; cpf: string; pagarme_charge_id?: string | null }) => api.post("/badge/asaas/card", d, { timeout: 90000 }),
  checkout: () => api.post("/badge/checkout", {}),
  check: (d: { order_nsu: string }) => api.post("/badge/check", d),
  status: () => api.get("/badge/status"),
  cancel: () => api.post("/badge/cancel", {}),
  joinWaitlist: (source: "fila" | "pagina") => api.post("/badge/waitlist", { source }),

  // Pagar.me — selo de verificação do médico (checkout transparente)
  pagarmeCreate: (d: Record<string, unknown>) =>
    api.post("/badge/pagarme/create", d),
  pagarmeCheck: (d: Record<string, unknown>) =>
    api.post("/badge/pagarme/check", d),
  pagarmeCancelSubscription: () =>
    api.post("/badge/pagarme/cancel-subscription", {}),
  cancelFlow: () => api.get("/badge/cancel-flow"),
  cancelFlowEvent: (d: { event: string; reason?: string; text?: string }) => api.post("/badge/cancel-flow/event", d),
  cancelFlowOffer: (d: { reason?: string; text?: string }) => api.post("/badge/cancel-flow/offer", d),
  cancelFlowCancel: (d: { reason: string; text?: string; confirm: boolean }) => api.post("/badge/cancel-flow/cancel", d),
};

export const adminAPI = {
  // Blink+ (31/10/2026): assinantes e renovações de receita a marcar (equipe).
  premiumRetention: () => api.get("/admin/premium/retention"),
  blinkplus: () => api.get("/admin/blinkplus"),
  blinkplusExames: () => api.get("/admin/blinkplus/exames"),
  blinkplusHandled: (id: number) => api.post(`/admin/blinkplus/medications/${id}/handled`),
  blinkplusSync: (id: number) => api.post(`/admin/blinkplus/subscriptions/${id}/sync`, {}, { timeout: 60000 }),
  getDashboard: () => api.get("/admin/dashboard"),
  getMarketingOverview: (params?: Record<string, unknown>) =>
    api.get("/admin/marketing", { params }),

  getDoctors: (status?: string) => api.get("/admin/doctors", { params: status ? { status } : {} }),
  getPendingDoctors: () => api.get("/admin/doctors?status=pending"),
  approveDoctor: (
    id: string | number,
    d: { action: "approve" | "reject"; rejection_reason?: string; divergence_ok?: boolean; crm_check_override?: boolean }
  ) => api.patch(`/admin/doctors/${id}/approve`, d),
  getDoctorCertificateStatus: (id: string | number) => api.get(`/admin/doctors/${id}/certificate-status`),
  createDoctor: (d: Record<string, unknown>) => api.post("/admin/doctors", d),
  updateDoctor: (id: string | number, d: Record<string, unknown>) =>
    api.put(`/admin/doctors/${id}`, d),
  deleteDoctor: (id: string | number) => api.delete(`/admin/doctors/${id}`),

  getPatients: (params?: Record<string, unknown>) => api.get("/admin/patients", { params }),
  updatePatient: (id: string | number, d: Record<string, unknown>) =>
    api.put(`/admin/patients/${id}`, d),
  deletePatient: (id: string | number) => api.delete(`/admin/patients/${id}`),

  getQuizSubmissions: () => api.get("/admin/quiz-submissions"),
  updateQuizSubmission: (id: string | number, d: Record<string, unknown>) =>
    api.put(`/admin/quiz-submissions/${id}`, d),

  getOrders: (params?: Record<string, unknown>) => api.get("/admin/orders", { params }),
  updateOrder: (id: string | number, d: Record<string, unknown>) =>
    api.put(`/admin/orders/${id}`, d),
  deleteOrder: (id: string | number) => api.delete(`/admin/orders/${id}`),

  getChats: () => api.get("/admin/chats"),
  getAppointments: (params?: Record<string, unknown>) => api.get("/admin/appointments", { params }),
  getAppointment: (id: string | number) => api.get(`/admin/appointments/${id}`),
  /** Admin cancela QUALQUER consulta (status ativo) — sem efeito em carteira. */
  cancelAppointment: (id: string | number, d: { reason?: string; reason_other?: string; notify?: boolean } = {}) => api.post(`/admin/appointments/${id}/cancel`, d),
  // Agendar consulta (sem cobrança) com o médico escolhido — admin e secretária (17/10/2026).
  createAppointment: (d: { patient_case_id: number | string; doctor_id: string; appointment_date: string; appointment_time: string; duration?: number; notes?: string; notify?: boolean }) =>
    api.post("/admin/appointments", d),
  // Ficha do médico sem documentos/CPF/financeiro (o que a secretária vê).
  getDoctorBasic: (id: string) => api.get(`/admin/doctors/${id}/basic`),
  // Equipe do /admin (secretária, marketing, nutricionista) — só admin.
  getTeam: () => api.get("/admin/team"),
  createTeamMember: (d: { name: string; email: string; password: string; role: string }) => api.post("/admin/team", d),
  updateTeamMember: (id: string, d: { name?: string; password?: string; active?: boolean }) => api.patch(`/admin/team/${id}`, d),
  saveTeamSettings: (d: { secretaria_reads_chats?: boolean }) => api.put("/admin/team/settings", d),
  getTeamMemberTerms: (id: string) => api.get(`/admin/team/${id}/terms`),
  /**
   * Admin desfaz consulta — estorna automaticamente o repasse do médico na carteira.
   * Com `{ allow_refund: true }` gera um cupom de estorno EXCLUSIVO do paciente.
   */
  undoAppointment: (id: string | number, d?: Record<string, unknown>) =>
    api.post(`/admin/appointments/${id}/undo`, d || {}),
  getPrescriptions: () => api.get("/admin/prescriptions"),
  getAuditLogs: (params?: Record<string, unknown>) => api.get("/admin/audit-logs", { params }),

  getReceipts: (params?: { limit?: number; status?: string }) => api.get("/admin/receipts", { params }),
  refundReceipt: (id: string | number, d?: Record<string, unknown>) =>
    api.post(`/admin/receipts/${id}/refund`, d || {}),
  getFinancialSummary: () => api.get("/admin/financial-summary"),

  adjustReceipt: (id: string | number, d: Record<string, unknown>) =>
    api.post(`/admin/receipts/${id}/adjust`, d),
  adjustWallet: (doctorId: string | number, d: Record<string, unknown>) =>
    api.post(`/admin/wallets/${doctorId}/adjust`, d),
  archivePatient: (id: string | number, d: Record<string, unknown>) =>
    api.put(`/admin/patients/${id}/archive`, d),
  unarchivePatient: (id: string | number, d: Record<string, unknown>) =>
    api.put(`/admin/patients/${id}/unarchive`, d),

  // Cancelamentos da fila de plantão (quem desistiu de esperar). `days` = janela
  // de busca (1-90, default 7 no backend).
  getCanceledQueue: (params?: Record<string, unknown>) =>
    api.get("/admin/queue/canceled", { params }),

  // Cancelamentos unificados (fila + consultas agendadas) — aba "Canceladas" de
  // Consultas. `days` = janela de busca (1-365, default 30 no backend).
  // Aba Premium: médicos com Premium + conferência no Pagar.me.
  getPremium: () => api.get("/admin/premium"),
  getPremiumSales: () => api.get("/admin/premium/sales"),
  getPremiumWaitlist: () => api.get("/admin/premium/waitlist"),
  // Avaliações dos pacientes sobre os médicos (moderação).
  observeRoom: (appointmentId: number | string) => api.post(`/admin/video/observe/${appointmentId}`, {}),
  getReviews: (params?: Record<string, unknown>) => api.get("/admin/reviews", { params }),
  setReviewHidden: (id: number, hidden: boolean, reason?: string) => api.patch(`/admin/reviews/${id}`, { hidden, reason }),
  deleteReview: (id: number) => api.delete(`/admin/reviews/${id}`),
  // ── Operação (16/10/2026): fila ao vivo, ações na consulta, paciente, suporte, entrar como, saúde ──
  getOpsBoard: () => api.get("/admin/ops/board"),
  queueAssign: (queueId: string | number, doctorId: string) => api.post(`/admin/ops/queue/${queueId}/assign`, { doctor_id: doctorId }),
  queueRemove: (queueId: string | number, notify: boolean) => api.post(`/admin/ops/queue/${queueId}/remove`, { notify }),
  queueNotifyGroup: (queueId: string | number) => api.post(`/admin/ops/queue/${queueId}/notify-group`, {}),
  queueRequeue: (d: { patient_case_id: string | number; order_id?: string | number | null; notify?: boolean }) => api.post("/admin/ops/queue/requeue", d),
  apptReassign: (id: string | number, doctorId: string, notify = true) => api.post(`/admin/appointments/${id}/reassign`, { doctor_id: doctorId, notify }),
  apptReschedule: (id: string | number, d: { appointment_date: string; appointment_time: string; notify?: boolean }) => api.post(`/admin/appointments/${id}/reschedule`, d),
  apptComplete: (id: string | number, d: { pay_doctor: boolean; note?: string }) => api.post(`/admin/appointments/${id}/complete`, d),
  apptRedo: (id: string | number, d: { mode: "queue" | "doctor"; doctor_id?: string; notify?: boolean }) => api.post(`/admin/appointments/${id}/redo`, d),
  apptResendLink: (id: string | number, to: "patient" | "doctor" | "both", reason?: "finalize") => api.post(`/admin/appointments/${id}/resend-link`, { to, reason }),
  apptRestartVideo: (id: string | number) => api.post(`/admin/appointments/${id}/restart-video`, {}),
  apptEvents: (id: string | number) => api.get(`/admin/appointments/${id}/events`),
  apptMessages: (id: string | number) => api.get(`/admin/appointments/${id}/messages`),
  setTestFlag: (kind: "patient" | "appointment", id: string | number, isTest: boolean) => api.post("/admin/test-flag", { kind, id, is_test: isTest }),
  getNotes: (entityType: "patient" | "appointment" | "doctor", entityId: string | number) => api.get("/admin/notes", { params: { entity_type: entityType, entity_id: entityId } }),
  addNote: (entityType: "patient" | "appointment" | "doctor", entityId: string | number, body: string) => api.post("/admin/notes", { entity_type: entityType, entity_id: entityId, body }),
  deleteNote: (id: string | number) => api.delete(`/admin/notes/${id}`),
  patientAccessLink: (caseId: string | number, send: "whatsapp" | "email" | "none") => api.post(`/admin/patients/${caseId}/access-link`, { send }),
  patientCourtesy: (caseId: string | number, d: { note?: string; send?: boolean }) => api.post(`/admin/patients/${caseId}/courtesy`, d),
  patientFamily: (caseId: string | number) => api.get(`/admin/patients/${caseId}/family`),
  supportMessage: (caseId: string | number, d: { message: string; appointment_id?: string | number | null; notify?: boolean }) => api.post(`/admin/chats/${caseId}/message`, d),
  startImpersonation: (d: { kind: "patient" | "doctor"; id: string | number; reason: string }) => api.post("/admin/impersonate", d),
  getImpersonations: () => api.get("/admin/impersonations"),
  getSystemHealth: () => api.get("/admin/system-health"),
  /** days > 0 abre por N dias a partir de agora; days = 0 fecha na hora. */
  setPremiumSales: (days: number) => api.put("/admin/premium/sales", { days }),
  // Blink Emagrecimento — interessados (aba /admin → Emagrecimento)
  getEmagrecimento: () => api.get("/admin/emagrecimento"),
  createEmagrecimentoLead: (data: { name: string; phone: string; best_time?: string | null; note?: string }) =>
    api.post("/admin/emagrecimento/leads", data),
  updateEmagrecimentoLead: (id: string, data: { status?: string; name?: string; phone?: string; best_time?: string | null }) =>
    api.patch(`/admin/emagrecimento/leads/${id}`, data),
  addEmagrecimentoEvent: (id: string, data: { action: "nota" | "contato" | "link"; detail?: string }) =>
    api.post(`/admin/emagrecimento/leads/${id}/events`, data),
  deleteEmagrecimentoLead: (id: string) => api.delete(`/admin/emagrecimento/leads/${id}`),
  verifyPremium: (doctorId: string) => api.post(`/admin/premium/${doctorId}/verify`, {}),

  getCancelamentos: (params?: Record<string, unknown>) =>
    api.get("/admin/cancelamentos", { params }),
  markRefund: (origin: "appointment" | "queue", id: string | number, d: { status: string; notes?: string }) =>
    api.patch(`/admin/cancelamentos/${origin}/${id}/refund`, d),

  getWithdrawals: () => api.get("/admin/withdrawals"),
  getWithdrawalConsultations: (id: string | number) =>
    api.get(`/admin/withdrawals/${id}/consultations`),
  processWithdrawal: (id: string | number, d?: Record<string, unknown>) =>
    api.post(`/admin/withdrawals/${id}/process`, d || {}),
  getWallets: () => api.get("/admin/wallets"),
  // Financeiro (15/10/2026): agregados calculados no servidor
  // Busca global do /admin (paciente, médico, consulta) — 16/10/2026
  search: (q: string) => api.get("/admin/search", { params: { q } }),
  getFinance: (params?: { from?: string; to?: string; touch?: "first" | "last" }) => api.get("/admin/finance", { params }),
  // Início do /admin (24/10/2026): números do período prontos no servidor (inicioController.js)
  getInicio: (params?: { from?: string; to?: string }) => api.get("/admin/inicio", { params }),
  // Aba secreta "Futuro" (24/10/2026): série real + ritmo estimado (futuroController.js)
  getFuturo: () => api.get("/admin/futuro"),
  // Lembretes por e-mail para médicos (Médicos → Lembretes, 24/10/2026)
  getDoctorNudges: () => api.get("/admin/medicos/lembretes"),
  saveDoctorNudgesConfig: (d: { enabled?: boolean; daily_limit?: number }) => api.put("/admin/medicos/lembretes/config", d),
  previewDoctorNudge: (doctorId: string) => api.get(`/admin/medicos/lembretes/previa/${doctorId}`),
  pauseDoctorNudge: (doctorId: string, paused: boolean) => api.post(`/admin/medicos/lembretes/${doctorId}/pausar`, { paused }),
  // E-mails automáticos para pacientes (24/10/2026) — Comunicação → E-mails automáticos
  getPatientEmails: () => api.get("/admin/pacientes/emails"),
  // Resultados de WhatsApp e e-mail automáticos (01/11/2026, services/commResults.js).
  getCommResults: (days: number) => api.get("/admin/comunicacao/resultados", { params: { days } }),
  savePatientEmailsConfig: (d: { enabled?: boolean; daily_limit?: number; control_pct?: number; flows?: Record<string, boolean> }) => api.put("/admin/pacientes/emails/config", d),
  previewPatientEmail: (flow: string, pid?: number | string) => api.get("/admin/pacientes/emails/previa", { params: { flow, pid } }),
  pausePatientEmail: (pid: number | string, paused: boolean) => api.post(`/admin/pacientes/emails/${pid}/pausar`, { paused }),
  // WhatsApp automático para pacientes (25/10/2026) — Comunicação → WhatsApp automático
  // Caixa de conversas do WhatsApp (26/10/2026) — canal "evo" (Blink Evo, suporte) ou "meta" (oficial, avisos)
  inboxChats: (canal: "evo" | "meta", q?: string) => api.get(`/admin/inbox/${canal}/chats`, { params: q ? { q } : {} }),
  inboxMessages: (canal: "evo" | "meta", id: string) => api.get(`/admin/inbox/${canal}/chats/${encodeURIComponent(id)}`),
  inboxMedia: (canal: "evo" | "meta", id: string) => api.get(`/admin/inbox/${canal}/media/${encodeURIComponent(id)}`, { responseType: "blob", timeout: 60000 }),
  inboxSend: (canal: "evo" | "meta", id: string, text: string, file?: File | null) => {
    const fd = new FormData();
    if (text) fd.append("text", text);
    if (file) fd.append("file", file);
    return api.post(`/admin/inbox/${canal}/chats/${encodeURIComponent(id)}/send`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
  },
  // Atendente IA no WhatsApp de suporte (26/10/2026)
  getSupportAgent: () => api.get("/admin/atendente-ia"),
  saveSupportAgentConfig: (d: Record<string, unknown>) => api.put("/admin/atendente-ia/config", d),
  getSupportAgentConversation: (key: string) => api.get(`/admin/atendente-ia/conversa/${encodeURIComponent(key)}`),
  supportAgentAction: (key: string, acao: "devolver_ia" | "pausar_ia") => api.post(`/admin/atendente-ia/conversa/${encodeURIComponent(key)}/acao`, { acao }),
  simulateSupportAgent: (mensagens: { role: "contact" | "ai"; text: string }[], telefone?: string) =>
    api.post("/admin/atendente-ia/simular", { mensagens, telefone }, { timeout: 120000 }),
  getWhatsappFlows: () => api.get("/admin/whatsapp-fluxos"),
  saveWhatsappFlowsConfig: (d: Record<string, unknown>) => api.put("/admin/whatsapp-fluxos/config", d),
  resumeWhatsappFlows: () => api.post("/admin/whatsapp-fluxos/retomar"),
  syncWhatsappTemplates: () => api.post("/admin/whatsapp-fluxos/modelos/sincronizar"),
  submitAllWhatsappTemplates: () => api.post("/admin/whatsapp-fluxos/modelos/enviar-todos"),
  submitWhatsappTemplate: (name: string) => api.post(`/admin/whatsapp-fluxos/modelos/${encodeURIComponent(name)}/enviar`),
  resetWhatsappTemplate: (name: string) => api.post(`/admin/whatsapp-fluxos/modelos/${encodeURIComponent(name)}/padrao`),
  editWhatsappTemplate: (name: string, d: { body?: string; footer?: string | null; examples?: string[]; buttons?: { text: string }[] }) =>
    api.put(`/admin/whatsapp-fluxos/modelos/${encodeURIComponent(name)}`, d),
  testWhatsappTemplate: (name: string, phone: string) => api.post("/admin/whatsapp-fluxos/teste", { name, phone }),
  pauseWhatsappFlowsPatient: (pid: number | string, paused: boolean) => api.post(`/admin/whatsapp-fluxos/paciente/${pid}/pausar`, { paused }),
  getPayouts: (params?: { from?: string; to?: string }) => api.get("/admin/finance/payouts", { params }),
  // Planilha / fluxo de caixa mensal (18/10/2026) — financeSheetController.js
  getFinanceSheet: (month: string) => api.get("/admin/finance/sheet", { params: { month } }),
  createFinanceEntry: (d: Record<string, unknown>) => api.post("/admin/finance/entries", d),
  updateFinanceEntry: (id: number, d: Record<string, unknown>) => api.patch(`/admin/finance/entries/${id}`, d),
  deleteFinanceEntry: (id: number) => api.delete(`/admin/finance/entries/${id}`),
  copyFixedFinanceEntries: (month: string, ids?: number[]) => api.post("/admin/finance/entries/copy-fixed", { month, ids }),
  saveFinanceNotes: (month: string, notes: string) => api.put("/admin/finance/notes", { month, notes }),
  getWalletDetail: (doctorId: string | number) =>
    api.get(`/admin/wallets/${doctorId}`),

  getCoupons: () => api.get("/admin/coupons"),
  createCoupon: (d: Record<string, unknown>) => api.post("/admin/coupons", d),
  updateCoupon: (id: string | number, d: Record<string, unknown>) =>
    api.put(`/admin/coupons/${id}`, d),
  updateCouponCode: (id: string | number, code: string) =>
    api.put(`/admin/coupons/${id}/code`, { code }),
  deleteCoupon: (id: string | number) => api.delete(`/admin/coupons/${id}`),

  /** Estornos de consultas desfeitas — aba Estornos (seção Pacientes). */
  getEstornos: () => api.get("/admin/estornos"),

  getVideoDiagnostico: (appointmentId: string | number) =>
    api.get(`/admin/video/diagnostico/${appointmentId}`),

  broadcastWhatsapp: (d: Record<string, unknown>) =>
    api.post("/admin/broadcast/whatsapp", d),
  broadcastWhatsappSegmentado: (d: Record<string, unknown>) =>
    api.post("/admin/broadcast/whatsapp-segmentado", d),
  broadcastEmailSegmentado: (d: Record<string, unknown>) =>
    api.post("/admin/broadcast/email-segmentado", d),
  countPatientSegment: (params?: Record<string, unknown>) =>
    api.get("/admin/broadcast/segment-count", { params }),
  getSegmentRecipients: (params?: Record<string, unknown>) =>
    api.get("/admin/broadcast/segment-recipients", { params }),
  broadcastPush: (d: Record<string, unknown>) =>
    api.post("/admin/broadcast/push", d),
  bubbleWhatsStatus: (category?: "operacional" | "marketing") => api.get("/admin/bubblewhats/status", { params: category ? { category } : undefined }),
  bubbleWhatsQR: (category?: "operacional" | "marketing") => api.get("/admin/bubblewhats/qr", { params: category ? { category } : undefined }),
  // WhatsApp oficial (Meta, integrado direto — substituiu o Wablast em 18/10/2026): remarketing.
  metaWhatsappStatus: () => api.get("/admin/whatsapp-meta/status"),
  metaWhatsappMarkRead: () => api.post("/admin/whatsapp-meta/inbound/read"),
  // Envia um remarketing REAL de teste pelo canal configurado.
  sendRemarketingTest: (body: { to: string; name?: string }) => api.post("/admin/remarketing/test", body),
  bubbleWhatsRestart: (category?: "operacional" | "marketing") => api.post("/admin/bubblewhats/restart", { category }),
  sendTest: (d: Record<string, unknown>) => api.post("/admin/test-send", d),
  whatsappZapiStatus: () => api.get("/admin/whatsapp-zapi/status"),
  whatsappZapiTest: (d: Record<string, unknown>) => api.post("/admin/whatsapp-zapi/test", d),

  getSupportTickets: () => api.get("/support/tickets"),
  resolveTicket: (id: string | number, d?: Record<string, unknown>) =>
    api.put(`/support/tickets/${id}/resolve`, d || {}),

  getRemarketingLogs: (patientId: string | number) =>
    api.get(`/admin/remarketing/${patientId}`),
  sendRemarketing: (patientId: string | number, d: Record<string, unknown>) =>
    api.post(`/admin/remarketing/${patientId}`, d),

  // ── Central de Mensagens (aba "Mensagens") ──
  getMessageTemplates: () => api.get("/admin/messages/templates"),
  updateMessageTemplates: (d: Record<string, unknown>) =>
    api.put("/admin/messages/templates", d),
  createMessageType: (d: Record<string, unknown>) =>
    api.post("/admin/messages/types", d),
  deleteMessageType: (key: string) =>
    api.delete(`/admin/messages/types/${key}`),
  sendMessage: (patientId: string | number, d: Record<string, unknown>) =>
    api.post(`/admin/messages/${patientId}/send`, d),
  getMessageLogs: (params?: Record<string, unknown>) =>
    api.get("/admin/messages/logs", { params }),
  getAutoMessageSettings: () => api.get("/admin/messages/auto"),
  updateAutoMessageSettings: (d: Record<string, unknown>) =>
    api.put("/admin/messages/auto", d),
  testAutoMessageGroup: (d?: Record<string, unknown>) => api.post("/admin/messages/auto/test-group", d || {}),

  getSettings: () => api.get("/admin/settings"),
  updateSettings: (d: Record<string, unknown>) => api.patch("/admin/settings", d),
  restoreSettings: () => api.patch("/admin/settings", { restore_defaults: true }),
  updateDoctorPrice: (id: string | number, consultation_price: number) =>
    api.patch(`/admin/doctors/${id}/price`, { consultation_price }),

  // Admin edita a agenda e os valores do médico direto da ficha (aba "Agenda e
  // valores", só para APROVADOS). Preços por especialidade seguem via
  // PUT /admin/doctors/:id (updateDoctor) — specialty_prices + consultation_price.
  getDoctorSchedule: (id: string | number) => api.get(`/admin/doctors/${id}/schedule`),
  saveDoctorSchedule: (id: string | number, d: Record<string, unknown>) =>
    api.put(`/admin/doctors/${id}/schedule`, d),

  // CRM
  getCrmStatus: () => api.get("/admin/crm-status"),
  verifyCrm: (doctorId: string) => api.post(`/admin/doctors/${doctorId}/crm-verify`, {}),
  verifyCrmBatch: () => api.post("/admin/crm-verify-batch", {}),

  // Referral
  getReferralCodes: () => api.get("/admin/referral-codes"),

  // ── Credenciamento médico (análise admin) ──
  getCredenciamento: (params?: Record<string, unknown>) =>
    api.get("/admin/credenciamento", { params }),
  uploadDoctorRqeDocument: (id: string, especialidade: string, file: File) => {
    const fd = new FormData();
    fd.append("especialidade", especialidade);
    fd.append("file", file);
    return api.post(`/admin/doctors/${id}/rqe-documento`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  },
  getCredenciamentoDetail: (id: string | number) =>
    api.get(`/admin/credenciamento/${id}`),
  decideCredenciamento: (id: string | number, d: Record<string, unknown>) =>
    api.post(`/admin/credenciamento/${id}/decidir`, d),
  getCredReminder: (id: string | number) =>
    api.get(`/admin/credenciamento/${id}/lembrete`),
  sendCredReminder: (id: string | number, d: Record<string, unknown>) =>
    api.post(`/admin/credenciamento/${id}/lembrete`, d),
  updateCredenciamento: (id: string | number, d: Record<string, unknown>) =>
    api.put(`/admin/credenciamento/${id}`, d),
  conferenciaCfm: (id: string | number, d: Record<string, unknown>) =>
    api.post(`/admin/credenciamento/${id}/conferencia-cfm`, d),

  // ── Revalidações administrativas pós-aprovação ──
  getRevalidacoes: (params?: Record<string, unknown>) =>
    api.get("/admin/revalidacoes", { params }),
  getRevalidacaoDetail: (id: string | number) =>
    api.get(`/admin/revalidacoes/${id}`),
  decideRevalidacao: (id: string | number, d: Record<string, unknown>) =>
    api.post(`/admin/revalidacoes/${id}/decidir`, d),

  // ── Prontuários (Pacientes) ──
  getProntuarios: (params?: Record<string, unknown>) =>
    api.get("/admin/prontuarios", { params }),
  getPatientActivity: (params?: Record<string, unknown>) =>
    api.get("/admin/patients/activity", { params }),
  getProntuario: (id: string | number) =>
    api.get(`/admin/prontuarios/${id}`),

  // ── Iniciar consulta emergencial (Pacientes / Agenda) ──
  startConsultation: (d: Record<string, unknown>) =>
    api.post("/admin/consultations/start", d),
  sendToGroup: (d: Record<string, unknown>) =>
    api.post("/admin/consultations/send-to-group", d),
  sendToGroupOptions: (patient_case_id: number | string) =>
    api.get("/admin/consultations/send-to-group/options", { params: { patient_case_id } }),

  // ── Marketing consolidado (ETAPA 3) — funil, origens, campanhas, pacientes
  // Relatório agregado do perfil de marketing (sem dados pessoais): cadastros, vendas e atendimentos.
  // Relatório de vendas por origem/mídia (16/10/2026): JSON para a prévia; o CSV baixa por fetch (precisa do token).
  getSalesReport: (params: { from?: string; to?: string }) => api.get("/marketing/sales-report", { params }),
  getMarketingReport: (params?: { from?: string; to?: string }) =>
    api.get("/marketing/report", { params }),
  getMarketing: (params?: Record<string, unknown>) =>
    api.get("/admin/marketing", { params }),
  // Painel /intel (inteligência): emagrecimento, novos × reconsultas, consultas por dia/horário.
  getIntel: (params?: Record<string, unknown>) =>
    api.get("/admin/intel", { params }),
  // Marketing → Pacientes e segmentos (24/10/2026): perfil completo de cada paciente + linha do tempo.
  getPatientIntel: (params?: { from?: string; to?: string }) => api.get("/marketing/intel", { params }),
  getPatientIntelTimeline: (id: number | string) => api.get(`/marketing/intel/patient/${id}`),
};

// ─── Payment API ──────────────────────────────────────────────────────────────

export const paymentAPI = {
  // InfinitePay — usado apenas no Selo de Verificação (badge_orders) do médico
  createLinkInfinitepay: (d: Record<string, unknown>) =>
    api.post("/payments/infinitepay/create", d),
  checkPaymentInfinitepay: (d: Record<string, unknown>) =>
    api.post("/payments/infinitepay/check", d),

  // Pagar.me — Checkout Transparente do paciente (Plantão + Agendada)
  createPagarme: (d: Record<string, unknown>) =>
    api.post("/payments/pagarme/create", d),
  checkPagarme: (d: Record<string, unknown>) =>
    api.post("/payments/pagarme/check", d),
  // Asaas: 2ª tentativa automática do cartão quando a Pagar.me recusa (02/11/2026). Vai com a chave do pedido.
  chargeAsaas: (d: { order_id: string | number } & Record<string, unknown>) =>
    api.post("/payments/asaas/charge", d, { params: { k: safeGetItem(`order_key_${d.order_id}`) || undefined }, timeout: 90000 }),
  checkAsaas: (orderId: string | number) =>
    api.post("/payments/asaas/check", { order_id: orderId }, { params: { k: safeGetItem(`order_key_${orderId}`) || undefined } }),

  applyCoupon: (d: { code: string; price?: number; order_total?: number; phone?: string; email?: string; scheduled?: boolean; doctor_id?: string; specialty?: string; consult_request_id?: number; consult_request_key?: string }) =>
    api.post("/coupons/apply", { doctor_id: d.doctor_id, specialty: d.specialty, code: d.code, price: d.price ?? d.order_total, phone: d.phone, email: d.email, scheduled: d.scheduled, consult_request_id: d.consult_request_id, consult_request_key: d.consult_request_key }),
  useCoupon: (d: { coupon_id: number }) =>
    api.post("/coupons/use", d),
};

// ─── Checkout API ─────────────────────────────────────────────────────────────

/** Blink Emagrecimento — rotas públicas (landing e checkout). */
export const emagrecimentoAPI = {
  createLead: (data: { name: string; phone: string; best_time?: string | null; consent: boolean; website?: string; attribution?: unknown; quiz?: { version: number; answers: Record<string, string> } | null }) =>
    api.post("/emagrecimento/leads", data),
  checkoutPrefill: (token: string) => api.get(`/emagrecimento/checkout/${encodeURIComponent(token)}`),
  // Parcelado no PIX Automático. Vai com a chave do pedido.
  pixConfig: () => api.get("/emagrecimento/pix-auto/config"),
  pixCreate: (d: { order_id: string; parcelas: number; cpf: string }) =>
    api.post("/emagrecimento/pix-auto", d, { params: { k: safeGetItem(`order_key_${d.order_id}`) || undefined }, timeout: 60000 }),
  pixStatus: (orderId: string) =>
    api.post("/emagrecimento/pix-auto/status", { order_id: orderId }, { params: { k: safeGetItem(`order_key_${orderId}`) || undefined } }),
  linkOrder: (token: string, orderId: string) =>
    api.post(`/emagrecimento/checkout/${encodeURIComponent(token)}/order`, { order_id: orderId }),
  /** Funil da landing ao pagamento, onde as pessoas param e de onde vêm (admin → Emagrecimento → Funil e origem). */
  adminFunnel: (dias: number) => api.get("/admin/emagrecimento/funil", { params: { dias } }),
};

export const checkoutAPI = {
  submitQuiz: (d: Record<string, unknown>) => api.post("/checkout/quiz", d),
  // Chave secreta do pedido (27/10/2026): o servidor só devolve os dados pessoais do pedido (nome, CPF, respostas) para
  // quem apresentar esta chave — fica guardada só neste navegador. Ver getOrder no backend.
  createOrder: (d: Record<string, unknown>) => api.post("/checkout/order", d).then((res) => {
    const o = res.data as { id?: number | string; access_key?: string } | undefined;
    if (o?.id && o?.access_key) safeSetItem(`order_key_${o.id}`, o.access_key);
    return res;
  }),
  updateOrder: (id: string | number, d: Record<string, unknown>) =>
    api.put(`/checkout/order/${id}`, d),
  getOrder: (id: string | number) => api.get(`/checkout/order/${id}`, { params: { k: safeGetItem(`order_key_${id}`) || undefined } }),
};

// ─── Queue API ────────────────────────────────────────────────────────────────

export const queueAPI = {
  afterPayment: (d: Record<string, unknown>) =>
    api.post("/queue/after-payment", d, { params: { k: d.order_id ? safeGetItem(`order_key_${d.order_id}`) || undefined : undefined } }),
  enter: (d: Record<string, unknown>) => api.post("/queue/enter", d, { params: { k: d.order_id ? safeGetItem(`order_key_${d.order_id}`) || undefined : undefined } }),
  status: (patientCaseId: string | number) =>
    api.get(`/queue/status/${patientCaseId}`),
  list: () => api.get("/queue/list"),
  accept: (queueId: string | number) =>
    api.post(`/queue/${queueId}/accept`, {}),
  cancel: (queueId: string | number) =>
    api.post(`/queue/${queueId}/cancel`, {}),
  takeNext: () => api.post("/queue/take-next", {}),
  /** Pagou e saiu da fila sem ser atendido: ainda pode voltar com o mesmo pagamento? (31/10/2026) */
  rejoinOffer: () => api.get("/queue/rejoin"),
  /** Volta para a fila com o mesmo pedido — o servidor confere o pagamento na Pagar.me antes. */
  rejoin: (d: { queue_id?: number }) => api.post("/queue/rejoin", d),
};

// ─── Analytics API ────────────────────────────────────────────────────────────

export const analyticsAPI = {
  trackEvent: (d: Record<string, unknown>) => api.post("/analytics/event", d),
  getEvents: () => api.get("/analytics/events"),
  metaCapiEvent: (d: Record<string, unknown>) => api.post("/analytics/meta-capi-event", d),
};

// ─── Schedule API ─────────────────────────────────────────────────────────────

export const scheduleAPI = {
  getSchedule: () => api.get("/doctor/schedule"),
  saveSchedule: (d: Record<string, unknown>) => api.put("/doctor/schedule", d),
  scheduleAppointment: (d: Record<string, unknown>) =>
    api.post("/consultation/schedule", d),
  getAvailableSlots: (doctorId: string, date: string) =>
    api.get(`/doctor/${doctorId}/available-slots`, { params: { date } }),
};

// ─── Chat / Consultation API ──────────────────────────────────────────────────

export const chatAPI = {
  sendMessage: (roomIdOrData: number | string | Record<string, unknown>, d?: Record<string, unknown>) => {
    if (typeof roomIdOrData === 'object' && !d) {
      // Called as sendMessage({patient_case_id, message, ...}) or sendMessage({room_id, ...})
      const body = roomIdOrData as Record<string, unknown>;
      return api.post("/consultation/message", {
        ...body,
        patient_case_id: body.patient_case_id || body.room_id,
        // appointment_id vincula a mensagem à consulta correta (prontuário do médico)
        appointment_id: body.appointment_id ?? null,
      });
    }
    // Called as sendMessage(roomId, {message, ...})
    return api.post("/consultation/message", {
      ...(d || {}),
      patient_case_id: roomIdOrData,
      room_id: roomIdOrData,
      appointment_id: (d && d.appointment_id != null) ? d.appointment_id : null,
    });
  },
  /** Resumo de mensagens não lidas do painel do médico — 1 request no lugar do
      N+1 de getMessages por sala (que deixava o /medico lento: dezenas de
      requests sequenciais a cada poll de 10s). Devolve, por sala do médico,
      o timestamp da última mensagem do paciente; o painel compara com o
      marcador local chat_last_seen_<roomId>. Ver doctorController.js →
      getChatUnreadSummary. */
  getUnreadSummary: () => api.get("/doctor/chat/unread-summary"),
  // markRead=true → o chat está ABERTO e visível: o backend marca as mensagens do outro lado
  // como "visualizadas" (2 checks azuis). Sem isso só marca "recebidas" (2 checks cinza).
  getMessages: (caseId: string | number, appointmentId?: string | number | null, markRead?: boolean) => {
    const params: Record<string, string | number> = {};
    if (appointmentId != null && appointmentId !== '') params.appointment_id = appointmentId;
    if (markRead) params.mark_read = 1;
    return api.get(`/consultation/${caseId}/messages`, { params: Object.keys(params).length ? params : undefined });
  },
  uploadDocument: (
    caseId: string | number,
    formData: FormData,
    onProgress?: (percent: number) => void
  ) =>
    api.post(`/consultation/${caseId}/document`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: onProgress
        ? (e) => {
            if (e.total && e.total > 0) {
              onProgress(Math.min(100, Math.round((e.loaded / e.total) * 100)));
            }
          }
        : undefined,
    }),
  getDocuments: (caseId: string | number, appointmentId?: string | number | null) => {
    const params = appointmentId != null && appointmentId !== '' ? { appointment_id: appointmentId } : undefined;
    return api.get(`/consultation/${caseId}/documents`, { params });
  },
  concludeConsultation: (caseId: string | number, d?: Record<string, unknown>) =>
    api.post(`/consultation/${caseId}/conclude`, d || {}),
  requestRefund: (caseId: string | number, d?: Record<string, unknown>) =>
    api.post(`/consultation/${caseId}/refund`, d || {}),
  cancelConsultation: (caseId: string | number, d?: Record<string, unknown>) =>
    api.post(`/consultation/${caseId}/cancel`, d || {}),
  /** Altera data/horário de uma consulta agendada (até 1h antes do horário atual). */
  rescheduleAppointment: (
    appointmentId: string | number,
    d: { appointment_date: string; appointment_time: string }
  ) =>
    api.post(`/consultation/${appointmentId}/reschedule`, d),
};

// ─── Emergency API ────────────────────────────────────────────────────────────

export const emergencyAPI = {
  enter: (d: Record<string, unknown>) => api.post("/queue/enter", d, { params: { k: d.order_id ? safeGetItem(`order_key_${d.order_id}`) || undefined : undefined } }),
  status: (patientCaseId: string | number) =>
    api.get(`/queue/status/${patientCaseId}`),
};

// ─── Review API ───────────────────────────────────────────────────────────────

export const reviewAPI = {
  createReview: (d: Record<string, unknown>) => api.post("/reviews", d),
  getDoctorReviews: (doctorId: string | number) =>
    api.get(`/reviews/doctor/${doctorId}`),
  getMyReviews: () => api.get("/reviews/patient"),
};

// ─── Wallet API ───────────────────────────────────────────────────────────────

export const walletAPI = {
  getWallet: () => api.get("/doctor/wallet"),
  requestWithdrawal: (d: Record<string, unknown>) =>
    api.post("/doctor/wallet/withdraw", d),
  updatePixKey: (d: { pix_key: string }) => api.put("/doctor/pix-key", d),
};

// ─── Audit API ────────────────────────────────────────────────────────────────

// Termo de confidencialidade da equipe do /admin (secretária assina no 1º acesso — 17/10/2026).
export const teamTermAPI = {
  get: () => api.get("/team/term"),
  accept: (d: { term_version: string; signer_name: string; signer_cpf: string; agree: boolean }) => api.post("/team/term/accept", d),
};

export const auditAPI = {
  log: (d: Record<string, unknown>) => api.post("/audit-log", d),
};

// ─── Assinatura digital própria (26/10/2026 — substituiu o D4Sign) ────────────

export type SignedDocType =
  | "receita" | "receita_retencao" | "receita_controle"
  | "notificacao_a" | "notificacao_b" | "notificacao_b2"
  | "atestado" | "exame" | "relatorio";
export type SignMethod = "a1" | "vidaas" | "birdid" | "birdid_pro";

export interface SignedDocItem {
  product_id?: number | null;
  nome: string;
  concentracao?: string;
  forma?: string;
  quantidade?: string;
  posologia?: string;
}

export interface SignedDocPayload {
  itens?: SignedDocItem[];
  paciente?: { cpf?: string; passaporte?: string; endereco?: string };
  emitente?: { endereco?: string; telefone?: string };
  orientacoes?: string;
  indicacao?: string;
  texto?: string;
  dias_afastamento?: number;
  cid?: string;
  mostrar_endereco?: boolean;
}

export interface SignedDocRequest {
  appointment_id?: number | string | null;
  patient_case_id?: number | string | null;
  doc_type: SignedDocType;
  payload: SignedDocPayload;
}

// Memed aberta (01/11/2026): tempo com a Memed na tela e o que ela emitiu → /admin → Prescrições emitidas.
export const memedSessionAPI = {
  start: (appointmentId: number | string) => api.post("/doctor/memed-sessions", { appointment_id: appointmentId }),
  ping: (id: number, d: { delta: number; emitido?: string; fechar?: boolean }) => api.post(`/doctor/memed-sessions/${id}/ping`, d),
  /** Ao fechar a aba: fetch com keepalive (o navegador termina o envio mesmo com a página saindo). */
  pingFinal: (id: number, d: { delta: number; fechar: true }) => {
    try {
      const token = getToken();
      void fetch(`/api/doctor/memed-sessions/${id}/ping`, {
        method: "POST", keepalive: true,
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(d),
      }).catch(() => {});
    } catch { /* sem envio */ }
  },
};

// /admin → Consultas → Prescrições emitidas e /admin → Médicos → Receituários → Anvisa (01/11/2026).
export const prescricoesAdminAPI = {
  list: (params: { from?: string; to?: string; testes?: "1"; q?: string }) => api.get("/admin/prescricoes", { params }),
  anvisa: () => api.get("/admin/receituarios/anvisa"),
};

export const assinaturaAPI = {
  status: () => api.get("/doctor/assinatura/status"),
  // lembrarHoras: 0 (não lembrar), 6, 24 ou 168 (29/10/2026).
  uploadA1: (file: File, senha: string, lembrarHoras: number) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("senha", senha);
    fd.append("lembrar", lembrarHoras > 0 ? "true" : "false");
    fd.append("lembrar_horas", String(lembrarHoras));
    return api.post("/doctor/assinatura/a1", fd, { headers: { "Content-Type": "multipart/form-data" } });
  },
  removeA1: () => api.delete("/doctor/assinatura/a1"),
  forgetA1Password: () => api.delete("/doctor/assinatura/a1/senha"),
  connect: (provedor: string, returnTo: string) => api.post(`/doctor/assinatura/conectar/${provedor}`, { return_to: returnTo }),
  disconnect: (provedor: string) => api.delete(`/doctor/assinatura/sessao/${provedor}`),
  // BirdID Pro (CESS da Soluti, certificado A3 em nuvem) — POC só para o Dr. Felipe (28/10/2026).
  birdidProConnect: (d: { codigo: string; cpf?: string; horas?: number }) => api.post("/doctor/assinatura/birdid-pro/conectar", d),
  birdidProDisconnect: () => api.delete("/doctor/assinatura/birdid-pro"),
  birdidProTest: () => api.post("/doctor/assinatura/birdid-pro/teste", {}, { responseType: "blob" }),
  preview: (d: SignedDocRequest) => api.post("/doctor/documentos-assinados/previa", d, { responseType: "blob" }),
  // Prévia em imagens (uma por página), para caber na tela do celular (29/10/2026).
  previewImages: (d: SignedDocRequest) => api.post("/doctor/documentos-assinados/previa", d, { params: { formato: "imagens" } }),
  // lote: vários documentos assinados de uma vez — o paciente recebe um aviso só (29/10/2026).
  emit: (d: SignedDocRequest & { metodo: SignMethod; senha?: string; lembrar_horas?: number; enviar_paciente?: boolean; lote?: { indice: number; total: number } }) =>
    api.post("/doctor/documentos-assinados", d),
  list: (params: { patient_case_id?: number | string; appointment_id?: number | string; limit?: number }) =>
    api.get("/doctor/documentos-assinados", { params }),
  cancel: (id: number | string, motivo: string) => api.post(`/doctor/documentos-assinados/${id}/cancelar`, { motivo }),
  verify: (code: string) => api.get(`/public/documentos/${encodeURIComponent(code)}`),
  context: (appointmentId: number | string) => api.get("/doctor/documentos-assinados/contexto", { params: { appointment_id: appointmentId } }),
  // 28/10/2026: completar dados do receituário e documentos no prontuário.
  updatePatientData: (d: { appointment_id: number | string; cpf?: string; cep?: string; rua?: string; numero?: string; complemento?: string; bairro?: string; cidade?: string; uf?: string }) =>
    api.put("/doctor/documentos-assinados/paciente", d),
  updateEmitente: (d: { cep?: string; rua: string; numero: string; complemento?: string; bairro?: string; cidade: string }) =>
    api.put("/doctor/assinatura/emitente", d),
  listForCase: (caseId: number | string) => api.get(`/doctor/prontuario/${caseId}/documentos-assinados`),
  // Modelos de receita salvos pelo médico (29/10/2026).
  modelos: () => api.get("/doctor/assinatura/modelos"),
  salvarModelo: (d: { nome: string; itens: { nome: string; concentracao?: string; forma?: string; quantidade?: string; posologia?: string; categoria?: string }[] }) =>
    api.post("/doctor/assinatura/modelos", d),
  apagarModelo: (id: number | string) => api.delete(`/doctor/assinatura/modelos/${id}`),
  searchMeds: (q: string, limit?: number) => api.get("/meds/search", { params: { q, ...(limit ? { limit } : {}) } }),
  sncrStatus: () => api.get("/doctor/sncr/status"),
  sncrRequest: (d: { tipo: string; quantidade?: number; access_token: string }) => api.post("/doctor/sncr/numeracao", d),
  adminSncr: () => api.get("/admin/assinatura/sncr"),
  adminSaveSncr: (d: Record<string, unknown>) => api.put("/admin/assinatura/sncr", d),
  adminOverview: () => api.get("/admin/assinatura"),
  adminProviders: () => api.get("/admin/assinatura/provedores"),
  adminSaveProvider: (id: string, d: Record<string, unknown>) => api.put(`/admin/assinatura/provedores/${id}`, d),
};

// ─── Notifications API ────────────────────────────────────────────────────────

export const notificationsAPI = {
  // context ("doctor"/"patient") força o user_type correto no backend mesmo
  // quando o usuário logado tem role='admin' na tabela doctors (dono/dev
  // testando como médico) — sem isso, notificações de admin (ex.: "Fulano
  // solicitou saque") vazavam pra tela de notificações do médico. Ver
  // notificationController.js:getMyNotifications.
  getMyNotifications: (context?: "doctor" | "patient") => api.get("/notifications", { params: context ? { context } : undefined }),
  // context precisa ir também aqui (mesmo motivo do GET): sem ele, um admin
  // vendo como médico (role='admin' no JWT) nunca conseguia de fato marcar como
  // lidas as notificações do sino do /medico (gravadas com user_type='doctor') —
  // o backend filtrava por user_type='admin' e o UPDATE não batia nenhuma linha.
  markRead: (id: string | number, context?: "doctor" | "patient") => api.put(`/notifications/${id}/read`, {}, { params: context ? { context } : undefined }),
  markAllRead: (context?: "doctor" | "patient") => api.put("/notifications/read-all", {}, { params: context ? { context } : undefined }),
  /** Resolve uma notificação de onboarding (ex.: convite do grupo de médicos do WhatsApp). */
  dismissNotification: (id: string | number) => api.put(`/notifications/${id}/dismiss`, {}),
  markChatRead: (roomId: string | number, context?: "doctor" | "patient") => api.put(`/notifications/read-chat/${roomId}`, {}, { params: context ? { context } : undefined }),
  registerFCMToken: (d: { token: string }) => api.post("/fcm/register", d),
};

// ─── Support API ──────────────────────────────────────────────────────────────

export const supportAPI = {
  createTicket: (d: Record<string, unknown>) => api.post("/support/ticket", d),
  myTickets: () => api.get("/support/my-tickets"),
};

// ─── Terms API ────────────────────────────────────────────────────────────────

export const termsAPI = {
  getTerms: (type: string) => api.get(`/terms/${type}`),
  acceptTerms: (d: Record<string, unknown>) => api.post("/terms/accept", d),
  checkAcceptance: (params?: Record<string, unknown>) =>
    api.get("/terms/check", { params }),
};

// ─── Video API ────────────────────────────────────────────────────────────────

export const videoAPI = {
  getOrCreateRoom: (d: Record<string, unknown>) =>
    api.post("/video/room", d),
  endRoom: (d: Record<string, unknown>) => api.post("/video/end", d),
};

// ─── Blink+ (30/10/2026): área do assinante no portal. 1ª fase só para pacientes de teste. ───
export const blinkPlusAPI = {
  status: () => api.get("/patient/blinkplus/status"),
  promo: () => api.get("/patient/blinkplus/promo"),
  overview: () => api.get("/patient/blinkplus"),
  acceptConsent: () => api.post("/patient/blinkplus/consent"),
  /** 1 PDF ou até 4 fotos do mesmo exame (base64). A análise roda em segundo plano: consultar getExam até sair de "processando". */
  createExam: (files: { media_type: string; data: string }[]) =>
    api.post("/patient/blinkplus/exams", { files }, { timeout: 120000 }),
  getExam: (id: number | string) => api.get(`/patient/blinkplus/exams/${id}`),
  deleteExam: (id: number | string) => api.delete(`/patient/blinkplus/exams/${id}`),
  createHealth: (inputs: Record<string, unknown>) =>
    api.post("/patient/blinkplus/health", { inputs }, { timeout: 110000 }),
  getHealth: (id: number | string) => api.get(`/patient/blinkplus/health/${id}`),
  // Assinatura de verdade (31/10/2026, 2ª fase).
  /** Preço do Blink+ para a consulta do checkout (assinante: grátis/10%; ou assinar agora). Calculado no servidor. */
  quote: (params: { consult_request_id?: number | string; key?: string; night?: number; scheduled?: 1; list_price?: number }) =>
    api.get("/patient/blinkplus/quote", { params }),
  /** Checkout: salva o cartão para as cobranças mensais antes de cobrar a consulta do 1º mês. */
  prepare: (d: { order_id: number | string; card_token: string; cpf: string; terms_accepted: boolean }) =>
    api.post("/patient/blinkplus/prepare", d, { timeout: 60000 }),
  /** Página do Blink+: assina e paga o 1º mês na hora (com a família, se escolher). */
  subscribePix: (d: { cpf: string; dependents: number[]; terms_accepted: boolean }) =>
    api.post("/patient/blinkplus/subscribe-pix", d, { timeout: 60000 }),
  pixStatus: () => api.get("/patient/blinkplus/pix-status"),
  preparePix: (d: { order_id: number | string; cpf: string; terms_accepted: boolean }) => api.post("/patient/blinkplus/prepare-pix", d, { timeout: 60000 }),
  pixDrop: () => api.post("/patient/blinkplus/pix-drop", {}),
  switchPix: (d: { cpf: string }) => api.post("/patient/blinkplus/switch-pix", d, { timeout: 60000 }),
  subscribe: (d: { card_token: string; cpf: string; dependents: number[]; terms_accepted: boolean }) =>
    api.post("/patient/blinkplus/subscribe", d, { timeout: 60000 }),
  // 2ª operadora do cartão (Asaas): só quando a Pagar.me recusa.
  subscribeAsaas: (d: { card: unknown; cpf: string; dependents: number[]; terms_accepted: boolean }) =>
    api.post("/patient/blinkplus/subscribe-asaas", d, { timeout: 90000 }),
  cardAsaas: (d: { card: unknown; cpf: string }) => api.post("/patient/blinkplus/card-asaas", d, { timeout: 90000 }),
  cancel: (reason?: string) => api.post("/patient/blinkplus/cancel", { reason }, { timeout: 60000 }),
  changeCard: (d: { card_token: string; cpf: string }) => api.post("/patient/blinkplus/card", d, { timeout: 60000 }),
  addDependent: (patient_case_id: number) => api.post("/patient/blinkplus/dependents", { patient_case_id }, { timeout: 60000 }),
  removeDependent: (id: number) => api.delete(`/patient/blinkplus/dependents/${id}`, { timeout: 60000 }),
  medications: () => api.get("/patient/blinkplus/medications"),
  saveMedication: (d: { id?: number; patient_case_id: number; name: string; rx_type: string; expires_on: string; remind_days?: number }) =>
    d.id ? api.put(`/patient/blinkplus/medications/${d.id}`, d) : api.post("/patient/blinkplus/medications", d),
  deleteMedication: (id: number) => api.delete(`/patient/blinkplus/medications/${id}`),
};

export default api;
